import { ComponentType } from '@angular/cdk/overlay';
import {
  ApplicationRef,
  EmbeddedViewRef,
  Injectable,
  Injector,
  createComponent,
  inject,
  DOCUMENT,
} from '@angular/core';

import { focusElement } from '@plugins/client/utils';

import { DialogComponent } from './dialog.component';
import { COMMON_DIALOG_CONFIG, DIALOG_CONFIG, DIALOG_DATA } from './tokens';
import { DialogConfig } from './types';

const DEFAULT_DIALOG_CONFIG: DialogConfig = {
  modal: true,
  backdrop: false,
  verticalOffset: 25,
  verticalOffsetDisabled: false,
};

@Injectable({ providedIn: 'root' })
export class DialogService {
  #document = inject(DOCUMENT);
  #appRef = inject(ApplicationRef);
  #injector = inject(Injector);
  #commonConfig = inject(COMMON_DIALOG_CONFIG, { optional: true }) ?? {};

  open<T>(
    component: ComponentType<T>,
    data?: unknown,
    config: Partial<DialogConfig> = {},
  ) {
    const previousFocusedElement = this.#document
      .activeElement as HTMLElement | null;
    previousFocusedElement?.blur();

    const injector = Injector.create({
      parent: this.#injector,
      providers: [
        {
          provide: DIALOG_DATA,
          useValue: data,
        },
        {
          provide: DIALOG_CONFIG,
          useValue: {
            ...DEFAULT_DIALOG_CONFIG,
            ...this.#commonConfig,
            ...config,
          },
        },
      ],
    });
    const dialogRef = createComponent(DialogComponent, {
      environmentInjector: this.#appRef.injector,
      elementInjector: injector,
    });
    this.#appRef.attachView(dialogRef.hostView);
    const domElem = (dialogRef.hostView as EmbeddedViewRef<unknown>)
      .rootNodes[0] as HTMLElement;

    this.#document.body.appendChild(domElem);

    const close = () => {
      this.#appRef.detachView(dialogRef.hostView);
      dialogRef.destroy();
      focusElement(previousFocusedElement);
    };

    const componentRef = dialogRef.instance.open(component, close);
    return {
      dialog: dialogRef.instance,
      component: componentRef.instance,
      close,
    };
  }
}
